import { toChunks, type Gloss, type LessonBlock, type Locale } from '@diana/shared'
import type { LessonDetail, LessonExercise, VocabEntry } from '../../../lib/api.js'

/*
 * A lesson as the player walks it: a cover, then each part of the lesson in
 * short steps (word cards, one explanation per sub-heading, one dialogue per
 * scene, the listening player, one exercise per step), then a finish screen.
 * Homework (section 7) is its own screen, linked from the outline and the
 * finish step — except in Diana's teacher view, which walks it as a part.
 */

type Base = { id: string; sectionKey: string | null; label: string }

export type DialogueBlock = Extract<LessonBlock, { kind: 'dialogue' }>

export type Step =
  | (Base & { kind: 'cover' })
  | (Base & { kind: 'words'; entries: VocabEntry[]; group: 'slowo' | 'zwrot' })
  | (Base & { kind: 'text'; title: string; eyebrow: string; blocks: LessonBlock[]; glosses: Gloss[] })
  | (Base & { kind: 'listen'; title: string; part: string; scenes: DialogueBlock[]; glosses: Gloss[]; rest: LessonBlock[] })
  | (Base & { kind: 'exercise'; exercise: LessonExercise })
  | (Base & { kind: 'finish' })

export type OutlineGroup = { key: string; label: string; steps: { index: number; step: Step }[] }

/**
 * A part's name in the student's language. The section heading is written
 * „Gramatyka  /  Граматика  /  Грамматика”; the translated copy is the
 * fallback for headings written in one language.
 */
export function sectionLabel(section: { key: string; title: string }, locale: Locale): string {
  const parts = section.title.split(/\s{2,}\/\s{2,}|\s+\/\s+/).map((p) => p.trim())
  const at = locale === 'uk' ? 1 : locale === 'ru' ? 2 : 0
  return parts[at] || parts[0] || section.key
}

/** The part's Polish name, for the outline — the course is Polish, its signposts too. */
export function sectionLabelPl(section: { key: string; title: string }): string {
  return section.title.split(/\s{2,}\/\s{2,}|\s+\/\s+/)[0]?.trim() || section.key
}

export const HOMEWORK_KEY = 'praca-domowa'

/** "2A. Alfabet polski i wymowa" → "Alfabet polski i wymowa"; "Tekst: Trzy spotkania" → "Trzy spotkania". */
function shortTitle(title: string): string {
  return title
    .replace(/^\d+[A-Z]?\.\s+/, '')
    .replace(/^(Tekst|Text|Skrypt nagrania)\s*:\s*/i, '')
    .trim()
}

/** `homework`: walk section 7 too, as the teacher view does; a student does it on its own screen. */
export function buildSteps(lesson: LessonDetail, locale: Locale, { homework = false }: { homework?: boolean } = {}): Step[] {
  const steps: Step[] = [{ id: 'cover', kind: 'cover', sectionKey: null, label: lesson.title }]

  for (const section of lesson.sections) {
    if (!homework && (section.key === HOMEWORK_KEY || section.kind === 'homework')) continue
    const sk = section.key
    const partName = sectionLabelPl(section)

    if (section.kind === 'vocab') {
      const words = lesson.vocab.filter((v) => v.kind === 'slowo')
      const phrases = lesson.vocab.filter((v) => v.kind === 'zwrot')
      if (words.length > 0) steps.push({ id: `${sk}:slowa`, kind: 'words', sectionKey: sk, label: 'Karty słówek', entries: words, group: 'slowo' })
      if (phrases.length > 0) steps.push({ id: `${sk}:zwroty`, kind: 'words', sectionKey: sk, label: 'Zwroty', entries: phrases, group: 'zwrot' })
    }

    const chunks = toChunks(section.body).filter((c) => c.lang === null || c.lang === locale)

    if (section.kind === 'listening') {
      // The script is the recording: one player step for all its scenes.
      const scenes: DialogueBlock[] = []
      const glosses: Gloss[] = []
      const rest: LessonBlock[] = []
      for (const c of chunks) {
        for (const b of c.blocks) {
          if (b.kind === 'dialogue') scenes.push(b)
          else if (b.kind === 'glosses') glosses.push(...b.items)
          else rest.push(b)
        }
      }
      if (scenes.length > 0 || rest.length > 0) {
        steps.push({
          id: `${sk}:nagranie`,
          kind: 'listen',
          sectionKey: sk,
          label: 'Nagranie',
          title:
            scenes
              .map((sc) => sc.title.replace(/^Scena\s+\d+\s*[—–-]\s*/i, '').trim())
              .filter(Boolean)
              .join(' · ') || partName,
          part: partName,
          scenes,
          glosses,
          rest,
        })
      }
    } else {
      chunks.forEach((c, ci) => {
        const glosses = c.blocks.flatMap((b) => (b.kind === 'glosses' ? b.items : []))
        const dialogues = c.blocks.filter((b) => b.kind === 'dialogue')
        // A text of several scenes: one scene per step, glosses shared.
        if (dialogues.length >= 2) {
          let group: LessonBlock[] = []
          const groups: LessonBlock[][] = []
          for (const b of c.blocks) {
            if (b.kind === 'dialogue' && group.some((x) => x.kind === 'dialogue')) {
              groups.push(group)
              group = []
            }
            group.push(b)
          }
          groups.push(group)
          groups.forEach((g, gi) => {
            const d = g.find((b): b is DialogueBlock => b.kind === 'dialogue')
            steps.push({
              id: `${sk}:${ci}:${gi}`,
              kind: 'text',
              sectionKey: sk,
              label: d?.title ? shortTitle(d.title).replace(/^\d+\.\s*/, '') : `${shortTitle(c.title)} ${gi + 1}`,
              title: d?.title ? d.title.replace(/^\d+\.\s*/, '') : shortTitle(c.title),
              eyebrow: `dialog:${gi + 1}:${groups.length}`,
              blocks: g,
              glosses,
            })
          })
          return
        }
        if (c.blocks.length === 0) return
        steps.push({
          id: `${sk}:${ci}`,
          kind: 'text',
          sectionKey: sk,
          label: c.title ? shortTitle(c.title) : partName,
          title: c.title ? shortTitle(c.title) : partName,
          eyebrow: c.title ? `${partName}${/^\d+[A-Z]\./.test(c.title) ? ` · ${c.title.match(/^(\d+[A-Z])\./)![1]}` : ''}` : '',
          blocks: c.blocks,
          glosses,
        })
      })
    }

    for (const e of lesson.exercises.filter((x) => x.sectionKey === sk)) {
      steps.push({ id: `ex:${e.ref}`, kind: 'exercise', sectionKey: sk, label: `Ćw. ${e.ref} ${e.title}`.trim(), exercise: e })
    }
  }

  steps.push({ id: 'finish', kind: 'finish', sectionKey: null, label: '' })
  return steps
}

/** The left-hand outline: one group per part, in order. */
export function outlineOf(lesson: LessonDetail, steps: Step[]): OutlineGroup[] {
  const groups: OutlineGroup[] = []
  lesson.sections.forEach((s, i) => {
    // Section 7 has no steps unless the teacher view asked for them.
    if (steps.every((x) => x.sectionKey !== s.key)) return
    const mine = steps.map((step, index) => ({ index, step })).filter((x) => x.step.sectionKey === s.key)
    if (mine.length === 0) return
    groups.push({ key: s.key, label: `${i + 1} · ${sectionLabelPl(s)}`, steps: mine })
  })
  return groups
}
